"use client"

import ListingBenefits from "@/app/(listing)/lista-dig/_components/ListingBenefits"
import { ProgressIndicator } from "@/app/(listing)/lista-dig/_components/ProgressIndicator"
import { useListingStore } from "@/store/listing-store"
import { CustomBenefitItem } from "@/types/NonSyncedTypes"

import BankIDLogo from "@layout/icons/BankIDLogo"
import { listingMainCtaPressed, listingMainScreenShown } from "@lib/amplitude/helpers"
import { verifyAuth } from "@lib/auth"
import { Button } from "@ui-library/button/Button"
import PageContainer from "@ui-library/layout/PageContainer"
import { useRouter, useSearchParams } from "next/navigation"
import { useEffect, useState } from "react"
import { ImSpinner } from "react-icons/im"

const ListingMainScreen = ({
  customTitle,
  customItems
}: {
  customTitle?: string
  customItems?: CustomBenefitItem[]
}) => {
  const story = useListingStore.getState().story
  const data = story?.content
  const searchParams = useSearchParams()
  const [loading, setLoading] = useState(true)
  const { push } = useRouter()

  useEffect(() => {
    localStorage.removeItem("orginatedClinicId")
    if (searchParams?.get("clinicId")) {
      localStorage.setItem("orginatedClinicId", searchParams.get("clinicId") as string)
    }

    if (verifyAuth()) {
      push("/lista-dig/clinic")
    } else {
      localStorage.removeItem("sessionData")
      localStorage.removeItem("userRegionSupportsForms")
      setLoading(false)
      listingMainScreenShown()
    }
  }, [setLoading, searchParams, push])

  const handleBankIdClick = async () => {
    listingMainCtaPressed()
    const url = process.env.NEXT_PUBLIC_BASE_URL + "/api/v1/bankid/websession"

    if (!url) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      throw new (Error("No bankid url") as any)()
    }

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify({
          callbackType: process.env.NEXT_PUBLIC_BANKID_CALLBACK_TYPE,
          useTestBankId: false
        })
      })

      const data = await response.json()

      if (data.redirectUrl) {
        window.location.replace(data.redirectUrl)
      } else {
        push(`/lista-dig/error?error_code=${data.errors[0].status}&error_message=${data.errors[0].detail}`)
      }
    } catch (error) {
      return push("/lista-dig/error?error_code=500&error_message=Something went wrong")
    }
  }

  if (loading) {
    return (
      <div className="mt-24 flex min-h-[480px] flex-col items-center justify-center">
        <ImSpinner size={100} className="animate-spin" />
      </div>
    )
  }

  return (
    <>
      <PageContainer>
        <div className="mx-auto mt-8 flex w-full max-w-[800px] flex-col items-center justify-center bg-surface pt-4">
          <ProgressIndicator stepIndex={0} />
          <div className="items-left mt-14 flex w-[90%] max-w-[40rem] flex-col gap-4 text-left">
            <h2 className="text-on_surface">{data?.start_text}</h2>
            <p className="body--medium">{data?.start_preamble}</p>
          </div>

          <div className="py-12">
            <Button onClick={handleBankIdClick}>
              <span className="flex items-center gap-2 px-10 text-center">
                {data?.bankid_button_text}
                <BankIDLogo width={20} />
              </span>
            </Button>
          </div>

          <ListingBenefits
            title={customTitle ?? data?.listing_title}
            items={data?.listing_items}
            customItems={customItems}
          />
        </div>
      </PageContainer>
    </>
  )
}

export default ListingMainScreen
